/**
 * Branded inventory exporters with embedded photos.
 *
 * Builds:
 *   - Excel (.xlsx)  via exceljs — header band with two logos + property
 *                    title/subtitle + node label + date, photo column with
 *                    inline thumbnails, blue header row, alternating row bg.
 *   - PDF  (.pdf)    via jsPDF + jspdf-autotable — same branded header,
 *                    inline photo thumbnails drawn into each row's photo cell
 *                    using the `didDrawCell` callback.
 *
 * Branding (logos + title + subtitle) is editable from admin → Branding tab
 * and lives on the GET /api/settings doc. We always re-fetch settings before
 * exporting so admins see their tweaks reflected on the very next export.
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = [
  { key: 'po_number',          label: 'PO #' },
  { key: 'markit_reference',   label: 'Markit ref' },
  { key: 'name',               label: 'Item name' },
  { key: 'quantity_on_hand',   label: 'Qty on hand',   highlight: 'C8E6C9' },     // light green ARGB-friendly
  { key: 'quantity_requested', label: 'Qty requested', highlight: 'BBDEFB' },     // light blue
  { key: 'unit_price',         label: 'Unit price' },
  { key: 'shipping_cost',      label: 'Shipping cost' },
  { key: 'tax_duty',           label: 'Tax / Duty' },
  { key: 'currency',           label: 'Currency' },
  { key: 'description',        label: 'Description & specs' },
  { key: 'brand',              label: 'Brand' },
  { key: 'vendor',             label: 'Vendor' },
  { key: 'vendor_reference',   label: 'Vendor ref' },
  { key: 'source_market',      label: 'Source market' },
  { key: 'lead_time',          label: 'Lead time' },
  { key: 'shipping_method',    label: 'Shipping' },
  { key: 'freight_forwarder',  label: 'Freight forwarder' },
];

const SHIPPING_LABELS = { air: 'Air freight', sea: 'Sea freight', local: 'Local source' };
const formatValue = (key, raw) => {
  if (key === 'shipping_method') return SHIPPING_LABELS[raw] || raw || '';
  return raw || '';
};

// Resolve a possibly-relative URL to an absolute one we can fetch cross-origin.
const absUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) {
    // Site-relative paths under /api/uploads/* live on the backend; everything
    // else (e.g. /logo-hyatt-inclusive.jpg) is served from the frontend origin.
    if (u.startsWith('/api/')) return `${BACKEND}${u}`;
    if (typeof window !== 'undefined') return `${window.location.origin}${u}`;
  }
  return u;
};

// Fetch a URL and return { dataUrl, ext, width, height }. Resolves to null
// on any error so a single broken URL never aborts the whole export.
const fetchAsDataUrl = async (url) => {
  try {
    if (!url) return null;
    const resp = await fetch(absUrl(url), { mode: 'cors', credentials: 'omit' });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    // Only accept real images — a 200 OK returning an HTML error page would
    // otherwise embed garbage into the export.
    if (!blob.type || !blob.type.startsWith('image/')) return null;
    const ext = (blob.type.split('/')[1] || 'jpeg').replace('jpg', 'jpeg');
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    // Read intrinsic dimensions to keep aspect ratios in PDF
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => resolve({ w: 1, h: 1 });
      img.src = dataUrl;
    });
    return { dataUrl, ext: ext === 'svg+xml' ? 'png' : ext, width: dims.w, height: dims.h };
  } catch {
    return null;
  }
};

const fetchSettings = async () => {
  try {
    const { data } = await axios.get(`${API}/settings`);
    return data || {};
  } catch {
    return {};
  }
};

const todayStr = () => {
  const d = new Date();
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

// ───────────────────────── Excel ─────────────────────────
export async function exportInventoryExcel({ rows, nodeId, nodeLabel }) {
  const settings = await fetchSettings();
  const wb = new ExcelJS.Workbook();
  wb.creator = settings.export_title || 'Inventory';
  wb.created = new Date();
  const ws = wb.addWorksheet('Inventory', {
    pageSetup: {
      paperSize: 9,           // A4
      orientation: 'landscape',
      // Force the whole table to fit the width of ONE printed page.
      // `fitToHeight: 0` lets the report flow over multiple pages
      // vertically (rows), but never breaks horizontally — every column
      // is always visible on a single sheet of paper.
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.25, right: 0.25, top: 0.4, bottom: 0.4, header: 0.2, footer: 0.2 },
    },
  });

  // Column widths — tuned so the 14-column total stays within A4 landscape
  // (~245 character units at default Calibri 10). Photo column is 12 chars,
  // long text columns (description) wrap to two lines instead of widening.
  const COL_WIDTHS = {
    name: 16, description: 22, brand: 11, vendor: 11,
    quantity_on_hand: 8, quantity_requested: 9,
    unit_price: 9, shipping_cost: 10, tax_duty: 9, currency: 7,
    po_number: 8, markit_reference: 10,
    vendor_reference: 10, source_market: 11,
    lead_time: 9, shipping_method: 11, freight_forwarder: 12,
  };
  ws.columns = [
    { width: 11 },                 // Photo
    ...COLUMNS.map((c) => ({ width: COL_WIDTHS[c.key] || 12 })),
  ];

  // Reserve top rows for header band
  ws.getRow(1).height = 56;
  ws.getRow(2).height = 22;
  ws.getRow(3).height = 18;

  // Logos — embed if available
  const [leftLogo, rightLogo] = await Promise.all([
    fetchAsDataUrl(settings.export_logo_left),
    fetchAsDataUrl(settings.export_logo_right),
  ]);
  const lastColIdx = COLUMNS.length; // 0-based: last column index used for right logo anchor
  if (leftLogo) {
    const id = wb.addImage({ base64: leftLogo.dataUrl, extension: leftLogo.ext === 'jpeg' ? 'jpeg' : leftLogo.ext });
    ws.addImage(id, { tl: { col: 0, row: 0 }, ext: { width: 90, height: 56 } });
  }
  if (rightLogo) {
    const id = wb.addImage({ base64: rightLogo.dataUrl, extension: rightLogo.ext === 'jpeg' ? 'jpeg' : rightLogo.ext });
    ws.addImage(id, { tl: { col: lastColIdx, row: 0 }, ext: { width: 90, height: 56 } });
  }

  // Title band — span across the middle columns
  const titleStart = 'B1';
  const titleEnd = `${String.fromCharCode(65 + Math.max(2, lastColIdx - 1))}1`;
  ws.mergeCells(`${titleStart}:${titleEnd}`);
  const titleCell = ws.getCell('B1');
  titleCell.value = settings.export_title || 'Inventory';
  titleCell.font = { name: 'Calibri', size: 18, bold: true, color: { argb: 'FF003DA5' } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

  ws.mergeCells(`B2:${String.fromCharCode(65 + Math.max(2, lastColIdx - 1))}2`);
  const subCell = ws.getCell('B2');
  subCell.value = settings.export_subtitle || '';
  subCell.font = { name: 'Calibri', size: 11, color: { argb: 'FF4A5D5D' } };
  subCell.alignment = { vertical: 'middle', horizontal: 'center' };

  // Meta row: node label · count · date
  ws.mergeCells(`A3:${String.fromCharCode(65 + lastColIdx)}3`);
  const metaCell = ws.getCell('A3');
  metaCell.value = `${nodeLabel || nodeId} — ${rows.length} item${rows.length === 1 ? '' : 's'} · Generated ${todayStr()}`;
  metaCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF7A8C8C' } };
  metaCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };

  // Header row
  const headerRowIdx = 4;
  const headerLabels = ['Photo', ...COLUMNS.map((c) => c.label)];
  const headerRow = ws.getRow(headerRowIdx);
  headerRow.height = 22;
  headerLabels.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF003DA5' } };
    cell.border = {
      top:    { style: 'thin', color: { argb: 'FFD9E3F1' } },
      bottom: { style: 'thin', color: { argb: 'FFD9E3F1' } },
    };
  });

  // Pre-fetch all row photos in parallel (cap photo cell to 80×80 px)
  const photoData = await Promise.all(rows.map((r) => fetchAsDataUrl(r.photo_url)));

  // Data rows
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const rowIdx = headerRowIdx + 1 + i;
    const row = ws.getRow(rowIdx);
    row.height = 64;
    // Photo cell stays empty in the value; image is anchored on top
    row.getCell(1).value = '';
    COLUMNS.forEach((c, ci) => {
      const cell = row.getCell(ci + 2);
      cell.value = formatValue(c.key, r[c.key]);
      cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };
      cell.font = { name: 'Calibri', size: 10, color: { argb: 'FF1A2E2E' } };
      if (c.highlight) {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${c.highlight}` } };
      }
    });
    // Alternate row background
    if (i % 2 === 1) {
      for (let c = 1; c <= headerLabels.length; c++) {
        // Don't overwrite per-column highlights (green/blue qty cells).
        const colDef = COLUMNS[c - 2]; // c=1 is Photo (no COLUMNS entry)
        if (colDef && colDef.highlight) continue;
        row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF4F8FD' } };
      }
    }
    // Row border (bottom)
    for (let c = 1; c <= headerLabels.length; c++) {
      row.getCell(c).border = { bottom: { style: 'thin', color: { argb: 'FFEEF3F9' } } };
    }
    // Embed photo if present
    const p = photoData[i];
    if (p) {
      const imgId = wb.addImage({ base64: p.dataUrl, extension: p.ext === 'jpeg' ? 'jpeg' : p.ext });
      ws.addImage(imgId, {
        tl: { col: 0.1, row: rowIdx - 1 + 0.1 },
        ext: { width: 70, height: 56 },
        editAs: 'oneCell',
      });
    }
  }

  // Output
  const buf = await wb.xlsx.writeBuffer();
  const fname = `inventory-${(nodeLabel || nodeId).replace(/[^A-Za-z0-9-]+/g, '-').toLowerCase()}.xlsx`;
  saveAs(new Blob([buf], { type: 'application/octet-stream' }), fname);
}

// ───────────────────────── PDF ─────────────────────────
export async function exportInventoryPdf({ rows, nodeId, nodeLabel }) {
  const settings = await fetchSettings();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // Header band (white) — drawn on every page via didDrawPage callback below
  const [leftLogo, rightLogo] = await Promise.all([
    fetchAsDataUrl(settings.export_logo_left),
    fetchAsDataUrl(settings.export_logo_right),
  ]);

  // Pre-fetch photos
  const photoData = await Promise.all(rows.map((r) => fetchAsDataUrl(r.photo_url)));

  const drawHeader = () => {
    // Background band
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageW, 78, 'F');
    // Bottom hairline
    doc.setDrawColor(217, 227, 241);
    doc.setLineWidth(0.6);
    doc.line(20, 78, pageW - 20, 78);

    if (leftLogo) {
      const ratio = leftLogo.width / Math.max(leftLogo.height, 1);
      const h = 50;
      const w = Math.min(150, h * ratio);
      try { doc.addImage(leftLogo.dataUrl, leftLogo.ext.toUpperCase(), 24, 14, w, h); } catch { /* unsupported format */ }
    }
    if (rightLogo) {
      const ratio = rightLogo.width / Math.max(rightLogo.height, 1);
      const h = 50;
      const w = Math.min(150, h * ratio);
      try { doc.addImage(rightLogo.dataUrl, rightLogo.ext.toUpperCase(), pageW - w - 24, 14, w, h); } catch { /* unsupported format */ }
    }

    // Title centered
    doc.setTextColor(0, 61, 165);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(settings.export_title || 'Inventory', pageW / 2, 32, { align: 'center' });
    doc.setTextColor(74, 93, 93);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (settings.export_subtitle) {
      doc.text(settings.export_subtitle, pageW / 2, 47, { align: 'center' });
    }

    // Meta row beneath
    doc.setFontSize(9);
    doc.setTextColor(122, 140, 140);
    const meta = `${nodeLabel || nodeId} — ${rows.length} item${rows.length === 1 ? '' : 's'} · ${todayStr()}`;
    doc.text(meta, pageW / 2, 64, { align: 'center' });
  };

  drawHeader();

  // Build the table. The Photo column is left empty in `body` — we draw the
  // image inside `didDrawCell`. Other columns are formatted via formatValue.
  const head = [['Photo', ...COLUMNS.map((c) => c.label)]];
  const body = rows.map((r) => ['', ...COLUMNS.map((c) => formatValue(c.key, r[c.key]))]);

  // Build per-column auto-widths that ALWAYS sum to the available page
  // width, so the 14 columns never spill onto a second sheet. We start
  // with the relative weights below, then scale them to fill the printable
  // strip (pageW − left/right margins). Result: same proportions you'd
  // see on screen, just compressed to one page.
  const PDF_WEIGHTS = {
    name: 60, description: 100, brand: 40, vendor: 40,
    quantity_on_hand: 32, quantity_requested: 36,
    unit_price: 36, shipping_cost: 40, tax_duty: 34, currency: 30,
    po_number: 32, markit_reference: 40,
    vendor_reference: 36, source_market: 44,
    lead_time: 40, shipping_method: 46, freight_forwarder: 50,
  };
  const PHOTO_WEIGHT = 42;
  const sideMargin = 16;
  const availableW = pageW - sideMargin * 2;
  const weightSum = PHOTO_WEIGHT + COLUMNS.reduce((s, c) => s + (PDF_WEIGHTS[c.key] || 50), 0);
  const scale = availableW / weightSum;

  const columnStyles = {
    0: { cellWidth: PHOTO_WEIGHT * scale, halign: 'center' },
  };
  COLUMNS.forEach((c, i) => {
    columnStyles[i + 1] = { cellWidth: (PDF_WEIGHTS[c.key] || 50) * scale };
    // Highlight green/blue qty columns; #C8E6C9 light green, #BBDEFB light blue.
    if (c.highlight === 'C8E6C9') columnStyles[i + 1].fillColor = [200, 230, 201];
    if (c.highlight === 'BBDEFB') columnStyles[i + 1].fillColor = [187, 222, 251];
  });

  autoTable(doc, {
    startY: 90,
    head,
    body,
    // Force the table to span the same printable strip we computed widths
    // for, so jspdf-autotable doesn't second-guess us and add side gutters.
    tableWidth: availableW,
    margin: { top: 90, left: sideMargin, right: sideMargin, bottom: 30 },
    styles: { overflow: 'linebreak' },   // wrap long text instead of pushing the column wider
    headStyles: { fillColor: [0, 61, 165], textColor: 255, fontStyle: 'bold', fontSize: 7.5, cellPadding: 2 },
    bodyStyles: { fontSize: 7, valign: 'middle', cellPadding: 2, minCellHeight: 42 },
    alternateRowStyles: { fillColor: [244, 248, 253] },
    columnStyles,
    didDrawPage: () => {
      drawHeader();
    },
    didDrawCell: (data) => {
      if (data.section !== 'body' || data.column.index !== 0) return;
      const p = photoData[data.row.index];
      if (!p) return;
      const cell = data.cell;
      const padding = 3;
      const maxW = cell.width - padding * 2;
      const maxH = cell.height - padding * 2;
      const ratio = p.width / Math.max(p.height, 1);
      let w = maxW, h = maxW / ratio;
      if (h > maxH) { h = maxH; w = maxH * ratio; }
      const x = cell.x + (cell.width - w) / 2;
      const y = cell.y + (cell.height - h) / 2;
      try {
        doc.addImage(p.dataUrl, (p.ext || 'jpeg').toUpperCase(), x, y, w, h);
      } catch { /* unsupported format */ }
    },
  });

  const fname = `inventory-${(nodeLabel || nodeId).replace(/[^A-Za-z0-9-]+/g, '-').toLowerCase()}.pdf`;
  doc.save(fname);
}
